import { motion } from 'framer-motion';
import {
  Sparkles, ArrowRight, Target, FolderKanban, Flame, Check, ChevronRight, TrendingUp, TrendingDown,
} from 'lucide-react';
import { Chip, SectionTitle, ProgressBar } from '@/components/Primitives';
import { formatPLN, categoryChip, statusChip, priorityDot, formatDate } from '@/lib/format';
import { useLang } from '@/context/LanguageContext';
import { TEST_IDS } from '@/constants/testIds/app';
import { cn } from '@/lib/utils';

export const Section = ({ children, delay = 0 }) => (
  <motion.section
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.35, delay, ease: [0.16, 1, 0.3, 1] }}
  >
    {children}
  </motion.section>
);

const SeeAll = ({ onClick, label }) => (
  <button onClick={onClick} className="text-xs font-medium text-brand-cyan">{label}</button>
);

export const MainFocusCard = ({ mainFocus, onStart }) => {
  const { t } = useLang();
  return (
    <Section>
      <div
        data-testid={TEST_IDS.dashboard.mainFocus}
        className="relative overflow-hidden rounded-2xl border border-brand-cyan/20 bg-gradient-to-br from-brand-cyan/[0.12] via-brand-blue/[0.06] to-brand-violet/[0.08] p-5 shadow-[var(--shadow-card)]"
      >
        <div className="flex items-center gap-2 text-brand-cyan">
          <Sparkles className="h-4 w-4" />
          <span className="text-xs font-semibold uppercase tracking-wider">{t('dashboard.mainFocusEyebrow')}</span>
        </div>
        <h2 className="mt-3 font-display text-xl font-semibold leading-snug text-foreground">{mainFocus.title}</h2>
        <p className="mt-1.5 text-sm leading-6 text-foreground/65">{mainFocus.description}</p>
        <button
          data-testid="dashboard-main-focus-start"
          onClick={onStart}
          className="btn-brand mt-4 inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-semibold text-brand-ink"
        >
          {t('dashboard.startNow')} <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </Section>
  );
};

export const TopPriorities = ({ tasks, onComplete, onSeeAll }) => {
  const { t, tEnum, lang } = useLang();
  return (
    <Section delay={0.04}>
      <SectionTitle action={<SeeAll onClick={onSeeAll} label={t('dashboard.allTasks')} />}>{t('dashboard.topPriorities')}</SectionTitle>
      <div data-testid={TEST_IDS.dashboard.topPriorities} className="space-y-2">
        {tasks.length === 0 ? (
          <div className="glass-card rounded-2xl px-4 py-5 text-center text-sm text-foreground/55">
            {t('dashboard.topPrioritiesEmpty')}
          </div>
        ) : (
          tasks.map((task) => (
            <div key={task.id} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.05] px-3 py-3">
              <button
                data-testid={`dashboard-complete-task-${task.id}`}
                onClick={() => onComplete(task)}
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-white/20 text-transparent transition-colors hover:border-emerald-300 hover:text-emerald-300"
              >
                <Check className="h-3.5 w-3.5" />
              </button>
              <span className={cn('h-2 w-2 shrink-0 rounded-full', priorityDot(task.priority))} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-foreground/90">{task.title}</p>
                <p className="text-xs text-foreground/50">{tEnum(task.priority)} · {task.dueDate ? formatDate(task.dueDate, lang) : t('dashboard.noDate')}</p>
              </div>
            </div>
          ))
        )}
      </div>
    </Section>
  );
};

export const ActiveGoalsRow = ({ goals, onOpen, onSeeAll }) => {
  const { t, tEnum } = useLang();
  return (
    <Section delay={0.08}>
      <SectionTitle action={<SeeAll onClick={onSeeAll} label={t('dashboard.allGoals')} />}>{t('dashboard.activeGoals')}</SectionTitle>
      <div data-testid={TEST_IDS.dashboard.activeGoals} className="no-scrollbar -mx-1 flex gap-3 overflow-x-auto px-1 pb-1">
        {goals.length === 0 ? (
          <div className="glass-card w-full rounded-2xl px-4 py-5 text-center text-sm text-foreground/55">{t('dashboard.goalsEmpty')}</div>
        ) : (
          goals.map((g) => (
            <button key={g.id} onClick={onOpen}
              className="glass-card w-[220px] shrink-0 rounded-2xl p-4 text-left transition-transform active:scale-[0.99]">
              <div className="flex items-center justify-between">
                <Chip className={categoryChip(g.category)}>{tEnum(g.category)}</Chip>
                <Target className="h-4 w-4 text-brand-cyan/70" />
              </div>
              <p className="mt-2 line-clamp-2 text-sm font-semibold text-foreground/90">{g.title}</p>
              <div className="mt-3 flex items-center justify-between text-xs text-foreground/55">
                <span>{t('common.progress')}</span>
                <span className="font-mono-plex text-brand-cyan">{g.progress}%</span>
              </div>
              <ProgressBar value={g.progress} className="mt-1.5" />
            </button>
          ))
        )}
      </div>
    </Section>
  );
};

export const ProjectsAttention = ({ projects, onOpen, onSeeAll }) => {
  const { t, tEnum } = useLang();
  return (
    <Section delay={0.12}>
      <SectionTitle action={<SeeAll onClick={onSeeAll} label={t('dashboard.allProjects')} />}>{t('dashboard.projectsTitle')}</SectionTitle>
      <div data-testid={TEST_IDS.dashboard.projects} className="space-y-2">
        {projects.length === 0 ? (
          <div className="glass-card rounded-2xl px-4 py-5 text-center text-sm text-foreground/55">{t('dashboard.projectsEmpty')}</div>
        ) : (
          projects.map((p) => (
            <button key={p.id} onClick={() => onOpen(p)}
              className="flex w-full items-center gap-3 rounded-xl border border-white/10 bg-white/[0.05] px-3 py-3 text-left transition-transform active:scale-[0.99]">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-blue/15 text-brand-blue">
                <FolderKanban className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-foreground/90">{p.name}</p>
                <p className="truncate text-xs text-foreground/50">{t('common.next')} {p.nextAction || '—'}</p>
              </div>
              <Chip className={statusChip(p.status)}>{tEnum(p.status)}</Chip>
            </button>
          ))
        )}
      </div>
    </Section>
  );
};

export const FinanceSnapshot = ({ fin, onOpen }) => {
  const { t } = useLang();
  const remainingNegative = (fin.remaining ?? 0) < 0;
  return (
    <Section delay={0.16}>
      <SectionTitle action={<SeeAll onClick={onOpen} label={t('common.open')} />}>{t('dashboard.financeTitle')}</SectionTitle>
      <button data-testid={TEST_IDS.dashboard.finance} onClick={onOpen}
        className="glass-card block w-full rounded-2xl p-4 text-left transition-transform active:scale-[0.99]">
        <div className="grid grid-cols-3 gap-2">
          <div>
            <p className="text-[11px] text-foreground/50">{t('dashboard.income')}</p>
            <p className="font-mono-plex text-base font-semibold text-emerald-300">{formatPLN(fin.currentIncome)}</p>
          </div>
          <div>
            <p className="text-[11px] text-foreground/50">{t('dashboard.expenses')}</p>
            <p className="font-mono-plex text-base font-semibold text-foreground/90">{formatPLN(fin.fixedExpenses)}</p>
          </div>
          <div>
            <p className="text-[11px] text-foreground/50">{t('dashboard.remaining')}</p>
            <p className={cn('font-mono-plex text-base font-semibold', remainingNegative ? 'text-rose-300' : 'text-emerald-300')}>
              {formatPLN(fin.remaining)}
            </p>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between text-xs text-foreground/55">
          <span>{t('dashboard.monthlyIncomeGoal')}</span>
          <span className="font-mono-plex text-violet-300">{formatPLN(fin.monthlyIncomeGoal)}</span>
        </div>
        <ProgressBar value={fin.incomeGoalPct} tone="violet" className="mt-1.5" />
        <div className="mt-3 flex items-center gap-2 rounded-lg bg-white/[0.04] px-3 py-2 text-xs text-foreground/70">
          {remainingNegative ? <TrendingDown className="h-3.5 w-3.5 text-rose-300" /> : <TrendingUp className="h-3.5 w-3.5 text-emerald-300" />}
          <span className="line-clamp-1">{fin.nextMoneyAction}</span>
        </div>
      </button>
    </Section>
  );
};

export const HabitsToday = ({ habits, onToggle, onSeeAll }) => {
  const { t } = useLang();
  return (
    <Section delay={0.2}>
      <SectionTitle action={<SeeAll onClick={onSeeAll} label={t('dashboard.allHabits')} />}>{t('dashboard.habitsTitle')}</SectionTitle>
      <div data-testid={TEST_IDS.dashboard.habits} className="glass-card space-y-1.5 rounded-2xl p-3">
        {habits.length === 0 ? (
          <p className="px-1 py-3 text-center text-sm text-foreground/55">{t('dashboard.habitsEmpty')}</p>
        ) : (
          habits.map((h) => (
            <button key={h.id} data-testid={`dashboard-habit-toggle-${h.id}`} onClick={() => onToggle(h)}
              className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors hover:bg-white/[0.04]">
              <span className={cn('flex h-6 w-6 shrink-0 items-center justify-center rounded-md border transition-colors',
                h.completedToday ? 'border-emerald-300 bg-emerald-400/20 text-emerald-300' : 'border-white/20 text-transparent')}>
                <Check className="h-3.5 w-3.5" />
              </span>
              <span className={cn('flex-1 text-sm', h.completedToday ? 'text-foreground/45 line-through' : 'text-foreground/90')}>{h.name}</span>
              <span className="inline-flex items-center gap-1 text-xs text-amber-300/90"><Flame className="h-3.5 w-3.5" /> {h.streak}</span>
            </button>
          ))
        )}
      </div>
    </Section>
  );
};

export const AINextStep = ({ text, onAsk }) => {
  const { t } = useLang();
  return (
    <Section delay={0.24}>
      <div data-testid={TEST_IDS.dashboard.aiNextStep} className="relative overflow-hidden rounded-2xl border border-brand-violet/25 bg-gradient-to-br from-brand-violet/[0.10] to-white/[0.04] p-4">
        <div className="flex items-center gap-2 text-brand-violet">
          <Sparkles className="h-4 w-4" />
          <span className="text-xs font-semibold uppercase tracking-wider">{t('dashboard.aiNextStep')}</span>
        </div>
        <p className="mt-2 text-sm leading-6 text-foreground/85">{text}</p>
        <button data-testid="dashboard-ai-open" onClick={onAsk}
          className="mt-3 inline-flex items-center gap-1.5 rounded-xl border border-white/10 bg-white/[0.06] px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-white/[0.1]">
          {t('dashboard.askAssistant')} <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </Section>
  );
};
